import { useCurrentDay, useWindow } from '@krp/client-runtime'
import {
  CashiersScreen,
  SCHEDULE_WINDOWS,
  cashierChosen,
  cashierGridOf,
  cashierRemembered,
  combinedView,
  isMonthValue,
  monthCalendar,
  monthOfDay,
  scheduleAccess,
  scheduleAssignmentsAll,
  schedulePlaces,
  shiftMonth,
  type CashiersActions,
  type CashiersVm,
} from '@krp/schedule-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useCashierDirectory, useCellCommands, useShiftCashierId, viewOf } from './scheduleKit'

/**
 * «График по кассирам»: месяц — из адреса окна (`/schedule/cashiers/:month`, без месяца — текущий). Кассир — тот, кого выбрал
 * человек (слайс `chosen`, срок `screen`); пока он не выбран, предложен кассир открытой смены. Строки таблицы — ВСЕ активные
 * объекты; назначения всех объектов за месяц читаются одним запросом, из них же пометки «занято на другом объекте». Клик по
 * клетке — та же команда, что и в таблице объекта (`useCellCommands`).
 */
export function ScheduleCashiersBinding() {
  const dispatch = useAppDispatch()
  const { params, replaceWith } = useWindow()
  const today = useCurrentDay()
  const directory = useCashierDirectory()
  const commands = useCellCommands()
  const shiftId = useShiftCashierId(directory)
  const chosen = useAppSelector((state) => state.schedule.chosen.cashierId)
  const recent = useAppSelector((state) => state.schedule.recent.ids)

  const requested = params.month ?? ''
  const month = isMonthValue(requested) ? requested : monthOfDay(today)
  const calendar = monthCalendar(month, today)

  const access = scheduleAccess.useQuery()
  const places = schedulePlaces.useQuery({ includeArchived: true })
  const assignments = scheduleAssignmentsAll.useQuery({ from: calendar.from, to: calendar.to })

  const chosenId = chosen ?? shiftId
  const activePlaces = [...(places.data ?? [])]
    .filter((place) => !place.archived)
    .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, 'ru'))
  const placeNameOf = (id: string): string => places.data?.find((item) => item.id === id)?.name ?? 'другой объект'
  const archivedPlaceIds = new Set((places.data ?? []).filter((place) => place.archived).map((place) => place.id.toLowerCase()))
  const goToMonth = (next: string) => replaceWith(SCHEDULE_WINDOWS.cashiers, { month: next })

  const load = combinedView([viewOf(access), viewOf(places), viewOf({ ...assignments, data: assignments.currentData })])
  const chosenName = chosenId === null ? null : directory.nameOf(chosenId)
  const vm: CashiersVm = {
    load,
    refreshing: load.refreshing,
    editable: access.data?.canManage === true,
    title: calendar.title,
    days: calendar.days,
    rows:
      chosenId === null || chosenName === null
        ? []
        : cashierGridOf({
            days: calendar.days,
            cashierId: chosenId,
            cashierName: chosenName,
            places: activePlaces,
            items: assignments.currentData ?? [],
            saving: commands.pending,
            placeNameOf,
            archivedPlaceIds,
          }),
    error: commands.error,
    denied: commands.denied,
    people: directory.people,
    recent,
    chosenId,
    chosenName,
    fromShift: chosen === null && shiftId !== null,
    directoryError: directory.status === 'unavailable' ? directory.error : null,
  }
  const actions: CashiersActions = {
    previous: () => goToMonth(shiftMonth(month, -1)),
    next: () => goToMonth(shiftMonth(month, 1)),
    today: () => goToMonth(monthOfDay(today)),
    choose: (cashierId) => {
      commands.clear()
      dispatch(cashierChosen(cashierId))
      dispatch(cashierRemembered(cashierId))
    },
    toggle: commands.toggle,
    setKind: commands.setKind,
    dismissDenied: commands.dismissDenied,
    showPlaces: () => replaceWith(SCHEDULE_WINDOWS.home),
    reload: () => {
      void access.refetch()
      void places.refetch()
      void assignments.refetch()
    },
  }
  return <CashiersScreen vm={vm} actions={actions} />
}
